import { BarChart3, GitCompare, Globe, Sparkles, Zap } from "lucide-react";
import { ChangeIndicator } from "../common/ChangeIndicator";

export const ABTestReport = ({ report, t }) => {
  if (!report) return null;
  
  return (
    <div className="py-16 px-4 bg-gradient-to-b from-blue-50 to-white" data-testid="ab-test-report">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold mb-4">
            <GitCompare className="w-4 h-4" />
            {t.ab_test_report}
          </div>
          <h2 className="text-4xl font-black tracking-tight font-['Chivo'] mb-2">{t.compare_results}</h2>
          <p className="text-[var(--color-text-muted)]">
            {report.days_between} {t.days_between}
          </p>
        </div>
        
        {/* Main Score Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {/* Overall Score */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 text-center" data-testid="ab-overall-score">
            <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-4">Overall Score</h3>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <span className="text-2xl font-mono text-gray-400">{report.overall_score_change.before_value}</span>
                <p className="text-xs text-gray-400">{t.before}</p>
              </div>
              <div className="text-3xl text-gray-300">→</div>
              <div className="text-center">
                <span className="text-3xl font-mono font-bold text-[#a855f7]">{report.overall_score_change.after_value}</span>
                <p className="text-xs text-[var(--color-text-muted)]">{t.after}</p>
              </div>
            </div>
            <div className="mt-4">
              <ChangeIndicator change={report.overall_score_change.change} improved={report.overall_score_change.improved} />
            </div>
          </div>
          
          {/* AI Visibility */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 text-center" data-testid="ab-ai-visibility">
            <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-4">{t.ai_visibility}</h3>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <span className="text-2xl font-mono text-gray-400">{report.ai_visibility_change.before_value}</span>
                <p className="text-xs text-gray-400">{t.before}</p>
              </div>
              <div className="text-3xl text-gray-300">→</div>
              <div className="text-center">
                <span className="text-3xl font-mono font-bold text-[#a855f7]">{report.ai_visibility_change.after_value}</span>
                <p className="text-xs text-[var(--color-text-muted)]">{t.after}</p>
              </div>
            </div>
            <div className="mt-4">
              <ChangeIndicator change={report.ai_visibility_change.change} improved={report.ai_visibility_change.improved} />
            </div>
          </div>
          
          {/* Voice Search */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 text-center" data-testid="ab-voice-search">
            <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-4">{t.voice_search}</h3>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <span className="text-2xl font-mono text-gray-400">{report.voice_search_change.before_value}</span>
                <p className="text-xs text-gray-400">{t.before}</p>
              </div>
              <div className="text-3xl text-gray-300">→</div>
              <div className="text-center">
                <span className="text-3xl font-mono font-bold text-[#a855f7]">{report.voice_search_change.after_value}</span>
                <p className="text-xs text-[var(--color-text-muted)]">{t.after}</p>
              </div>
            </div>
            <div className="mt-4">
              <ChangeIndicator change={report.voice_search_change.change} improved={report.voice_search_change.improved} />
            </div>
          </div>
          
          {/* Zero-Click */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 text-center" data-testid="ab-zero-click">
            <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-4">{t.zero_click}</h3>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <span className="text-2xl font-mono text-gray-400">{report.zero_click_change.before_value}</span>
                <p className="text-xs text-gray-400">{t.before}</p>
              </div>
              <div className="text-3xl text-gray-300">→</div>
              <div className="text-center">
                <span className="text-3xl font-mono font-bold text-[#a855f7]">{report.zero_click_change.after_value}</span>
                <p className="text-xs text-[var(--color-text-muted)]">{t.after}</p>
              </div>
            </div>
            <div className="mt-4">
              <ChangeIndicator change={report.zero_click_change.change} improved={report.zero_click_change.improved} />
            </div>
          </div>
        </div>
        
        {/* Summary Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-green-50 border border-green-200 p-6 text-center">
            <div className="text-4xl font-mono font-bold text-green-600">{report.total_improvements}</div>
            <p className="text-sm text-green-700 mt-1">{t.improvements}</p>
          </div>
          <div className="bg-red-50 border border-red-200 p-6 text-center">
            <div className="text-4xl font-mono font-bold text-red-600">{report.total_regressions}</div>
            <p className="text-sm text-red-700 mt-1">{t.regressions}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-6 text-center">
            <div className="text-4xl font-mono font-bold text-[#a855f7]">{report.improvement_percentage}%</div>
            <p className="text-sm text-blue-700 mt-1">{t.improvement_rate}</p>
          </div>
        </div>
        
        {/* Detailed Changes */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* SEO Changes */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)]" data-testid="ab-seo-changes">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <BarChart3 className="w-5 h-5 text-[#a855f7]" />
                {t.seo_changes}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {report.seo_improvements.map((metric, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-sm text-[var(--color-text-muted)]">{metric.metric_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{metric.before_value}→{metric.after_value}</span>
                    <ChangeIndicator change={metric.change} improved={metric.improved} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* GEO Changes */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)]" data-testid="ab-geo-changes">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <Globe className="w-5 h-5 text-[#a855f7]" />
                {t.geo_changes}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {report.geo_improvements.map((metric, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-sm text-[var(--color-text-muted)]">{metric.metric_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{metric.before_value}→{metric.after_value}</span>
                    <ChangeIndicator change={metric.change} improved={metric.improved} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Performance Changes */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)]" data-testid="ab-performance-changes">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <Zap className="w-5 h-5 text-[#a855f7]" />
                {t.performance_changes}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {report.performance_improvements.map((metric, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-sm text-[var(--color-text-muted)]">{metric.metric_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{metric.before_value}→{metric.after_value}</span>
                    <ChangeIndicator change={metric.change} improved={metric.improved} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* AI Insights */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="ab-ai-insights">
          <h3 className="font-bold flex items-center gap-2 font-['Chivo'] mb-4">
            <Sparkles className="w-5 h-5 text-[#a855f7]" />
            {t.ai_insights}
          </h3>
          <p className="text-[var(--color-text)] leading-relaxed">{report.ai_insights}</p>
        </div>
      </div>
    </div>
  );
};
